import { convertToAge, getStorageItem } from "./utils";
import { mapping, options } from "../defaultValues/product";
import {
  DEFAULT_PARTNER_CODE,
  SLUG_PREMIUM,
  SLUG_SUM_ASSURED,
  SLUG_TAB_TERM,
  SLUG_EDUCATION_10TH,
} from "../constants";
import moment from "moment";
import { getUniqueListBy } from "./buyflow";

export const addRidersPayload = (riders, payload) => {
  // Add rider keys to payload if rider is selected
  Object.entries(riders).forEach(([key, value]) => {
    if (value) {
      payload.rider[key] = true;
    }
  });
};

export const buildLifeCoverageMapping = (step = 50000) => {
  const coverageMapping = {};

  for (let amount = 200000; amount <= 5000000; amount += step) {
    coverageMapping[amount] = {
      label: `${amount / 100000} Lacs`,
      title: `${amount / 100000}L`,
    };
  }

  return coverageMapping;
};

export const buildPremiumPayload = ({
  age,
  gender,
  optionId,
  paymentFrequency,
  pid,
  pt,
  ppt,
  premium,
  smoker,
  type,
  multiplier = "",
  corpus: sumassured = null,
  occupation = "Salaried",
  annualIncome: annualincome = 100000,
  riders = null,
}) => {
  const payload = {
    pid,
    type,
    age,
    gender,
    smoker,
    ccrider: false,
    cirider: false,
    adbrider: false,
    ccridersa: 50000,
    ciridersa: 50000,
    adbridersa: 50000,
    pt,
    ppt,
    premium,
    sumassured,
    optionId,
    paymentFrequency,
    multiplier,
    occupation,
    annualincome,
    rider: {},
  };

  // Add relevant riders to payload if value is true
  if (type === SLUG_TAB_TERM) {
    addRidersPayload(riders, payload);
  }

  if (!Object.keys(payload.rider).length) {
    payload.rider = "";
  }

  return payload;
};

export const buildProductCategoriesPayload = () => {
  return {
    partnerid: Number(getStorageItem("partnerId")),
    producttype: getStorageItem("userType"),
  };
};

export const buildProductDetailsPayload = () => {
  return {
    partnerid: Number(getStorageItem("partnerId")),
    producttype: getStorageItem("userType"),
    productcategory: getStorageItem("productCategory"),
  };
};

export const buildSavingGridPayload = () => {
  return {
    partnerid: Number(getStorageItem("partnerId")),
    producttype: getStorageItem("userType"),
    productcategory: getStorageItem("productCategory"),
  };
};

export const buildFetchAppConfigPayload = () => {
  return {
    partnerid: Number(getStorageItem("partnerId")),
  };
};

export const buildUpdateCustomerPayload = ({
  dob,
  gender,
  annualIncome = "",
  occupation = "",
  education = "",
}) => {
  return {
    cid: Number(getStorageItem("cid")),
    dob: moment(dob).format("DD/MM/YYYY"),
    gender,
    annualIncome,
    occupation,
    ...(education && {
      education: education,
    }),
  };
};

export const buildUpdateQuotationPayload = (
  {
    optionId,
    partner_branch_code = "",
    paymentFrequency: paymentfrequency,
    pid,
    pt = 15,
    ppt = 10,
    multiplier = "",
    type,
    riders = null,
  },
  {
    dealthSumAssured,
    premiumExcludingGst,
    premiumIncludingGst,
    quotationid,
    guaranteeSumAssured,
    annualPremium,
    basePremum: plan_base_premium = 0,
    ADBRPremium = 0,
    maturitySumAssured,
  },
  maxADBSumAssured
) => {
  const payload = {
    cid: Number(getStorageItem("cid")),
    deathsumassured: dealthSumAssured,
    ekyc_partner: getStorageItem("ekycPartner"),
    planOption: optionId === "2" ? SLUG_SUM_ASSURED : SLUG_PREMIUM,
    pid,
    premium: premiumIncludingGst,
    premiumWithoutGST: premiumExcludingGst,
    paymentfrequency,
    oid: Number(getStorageItem("oid")),
    sumassured: guaranteeSumAssured,
    mandate_premium: Math.ceil(premiumIncludingGst + premiumIncludingGst * 0.0),
    quotationid,
    uid: Number(getStorageItem("uid")),
    pt,
    ppt,
    multiplier,
    ...(partner_branch_code && {
      partner_branch_code: partner_branch_code,
    }),
    annualpremium: annualPremium,
    rider: {},
    maturity_sum_assured: maturitySumAssured,
  };

  if (type === SLUG_TAB_TERM) {
    Object.entries(riders).forEach(([key, value]) => {
      if (value) {
        payload.rider[key] = {
          plan_base_premium,
          premium: ADBRPremium,
          total_premium: premiumIncludingGst,
          sum_assured:
            guaranteeSumAssured >= maxADBSumAssured
              ? maxADBSumAssured
              : guaranteeSumAssured,
          pt,
          ppt,
        };
      }
    });
  }

  if (!Object.keys(payload.rider).length) {
    payload.rider = "";
  }

  return payload;
};

export const buildShareDocumentsPayload = ({
  body = null,
  email = null,
  mobile,
  language: lang = 0,
  partnerSlug = "aviva",
  productName,
  productType = "SAVINGS",
  subject = null,
}) => {
  return {
    body,
    email,
    lang,
    mobile,
    partnerSlug,
    productName,
    productType,
    subject,
  };
};

export const buildShareProductDocumentsPayload = ({
  mobile,
  language: lang = 0,
}) => {
  return {
    body: null,
    email: null,
    lang,
    mobile,
    partnerSlug: getStorageItem("partner") || "bom",
    productName:
      getStorageItem("productName") || "Aviva Bharat Parivar Vikas Yojana",
    productSlug: getStorageItem("productSlug") || "parivar-vikas",
    productType: "SAVINGS",
    subject: null,
  };
};

export const buildWrapperTokenHeader = () => {
  return {
    Authorization: `Basic ${btoa(
      `${process.env.REACT_APP_WRAPPER_API_USER}:${process.env.REACT_APP_WRAPPER_API_PASSWORD}`
    )}`,
  };
};

export const buildBenefitIllustrationPayload = (
  {
    age,
    corpus,
    dob,
    download = 0,
    email = "",
    gender,
    optionId,
    paymentFrequency,
    pid,
    ppt,
    premium,
    productName,
    pt,
    multiplier = "",
    occupation = "Salaried",
    annualincome = 100000,
    type,
    riders = null,
  },
  { premiumIncludingGst, premiumExcludingGst, annualPremium }
) => {
  const payload = {
    age,
    annualpremium: Number(annualPremium), // Number(premium) * 12,
    dob,
    download,
    email,
    gender,
    mobile: getStorageItem("mobile"),
    optionId,
    paymentFrequency: paymentFrequency,
    pid,
    ppt,
    premium: premiumExcludingGst,
    premiumwithGST: premiumIncludingGst,
    productName,
    pt,
    sumassured: corpus,
    timestamps: Math.floor(Date.now() / 1000),
    multiplier,
    occupation,
    annualincome,
    rider: {},
  };

  // Add relevant riders to payload if value is true
  if (type === SLUG_TAB_TERM) {
    addRidersPayload(riders, payload);
  }

  if (!Object.keys(payload.rider).length) {
    payload.rider = "";
  }

  return payload;
};

export const rebuildCalculatePremiumResponse = (
  data,
  productConfig = null,
  selectedProduct = null
) => {
  let TOTAL_MOD_PREM,
    TOTAL_TAX_MOD,
    MATURITY_BEN_BS_1,
    MATURITY_BEN_BS_2,
    DSA,
    MSA,
    ANN_PREM = "ANN_PREM",
    BASE_PREMIUM,
    MATURITY_SA = "MSA";

  if (data.data[0].hasOwnProperty("TOTAL_TAX_MOD")) {
    TOTAL_MOD_PREM = "TOTAL_MOD_PREM";
    TOTAL_TAX_MOD = "TOTAL_TAX_MOD";
    MATURITY_BEN_BS_1 = "MATURITY_BEN_BS_1";
    MATURITY_BEN_BS_2 = "MATURITY_BEN_BS_2";
    DSA = "DSA";
    MSA = "MSA";
    BASE_PREMIUM = "TOTAL_MOD_PREM";
  } else {
    TOTAL_MOD_PREM = "MODAL_PREM";
    TOTAL_TAX_MOD = "MODAL_PREM_TAX";
    MATURITY_BEN_BS_1 = "MSA";
    MATURITY_BEN_BS_2 = "MSA";
    DSA = "DSA";
    MSA = "MSA";
    BASE_PREMIUM = "MODAL_PREM";
  }

  // This configuration is available only for term product as of 03-02-2026
  if (selectedProduct?.category === SLUG_TAB_TERM) {
    TOTAL_MOD_PREM = "MODAL_PREM_TOTAL";
    TOTAL_TAX_MOD = "MODAL_PREM_TOTAL";
    MATURITY_BEN_BS_1 = "DEATH_BENEFIT";
    MATURITY_BEN_BS_2 = "DEATH_BENEFIT";
    DSA = "DEATH_BENEFIT";
    MSA = "DEATH_BENEFIT";
    BASE_PREMIUM = "MODAL_PREM";
    MATURITY_SA = "MATURITY_SA";
  }

  const result = {
    asterisk: data.data[0].hasOwnProperty("TOTAL_TAX_MOD"),
    quotationid: data.QuotationId,
    premiumIncludingGst: Math.round(Number(data.data[0][TOTAL_TAX_MOD])),
    premiumExcludingGst: Math.round(Number(data.data[0][TOTAL_MOD_PREM])),
    dealthSumAssured: Math.round(data.data[0][DSA]),
    guaranteeSumAssured: Math.round(data.data[0][MSA]),
    assuredSumAssured: Math.round(data.data[0][MSA]),
    maturitySumAssured: Math.round(data.data[0][MATURITY_SA]),

    maturityTerminalBonus1: Math.round(
      data.data[data.data.length - 1][MATURITY_BEN_BS_1]
    ),
    maturityTerminalBonus2: Math.round(
      data.data[data.data.length - 1][MATURITY_BEN_BS_2]
    ),
    annualPremium: Math.round(Number(data.data[0][ANN_PREM])),
    basePremum: Math.round(Number(data.data[0][BASE_PREMIUM])),
  };

  // TODO: Change this logic once received clarity from backend team
  result.ADBRPremium = result.premiumExcludingGst - result.basePremum;

  return result;
};

export const convertStringToList = (input = null, suffix = "") => {
  if (!input) {
    return [];
  }

  const list = input.split(",").map((item) => ({
    value: item.trim(),
    title: `${item.trim()}${suffix}`,
  }));

  return list;
};

export const traverseProductDetailsResponse = (
  data,
  selectedProductId = null
) => {
  const productObject = {};
  const premiumOptions = [];
  const corpusOptions = [];
  const products = getUniqueListBy(data, "id");
  const filteredList = selectedProductId
    ? data.filter((item) => item.id == selectedProductId)
    : data.filter((item) => item.isdefault == 1);

  let productConfig = null;

  filteredList.map((item) => {
    if (item.premium > 0) {
      premiumOptions.push({
        title: mapping.premium[item.premium * item.multiplier].title,
        value: item.premium,
      });
    } else if (item.sum_assured > 0) {
      corpusOptions.push({
        title: item.sum_assured,
        value: item.sum_assured,
      });
    }

    selectedProductId = item.id;
    productConfig = item.product_config
      ? JSON.parse(item.product_config)
      : null;
    productObject[item.id] = item;
  });

  const {
    category,
    id: pid,
    paymentfrequency: paymentFrequency,
    name: productName,
    slug: productSlug,
    defaultcustommultiplier = "",
    paymentfrequencylist = "",
    custommultiplier = "",
    pptlist = "",
    ppt_pt_mapper = null,
    ppt,
    pt,
  } = productObject[selectedProductId];

  return {
    category,
    paymentFrequency,
    pid,
    premiumOptions,
    corpusOptions,
    productName,
    productSlug,
    type: category,
    multiplier: defaultcustommultiplier
      ? defaultcustommultiplier.toString()
      : "",
    paymentFrequencyList: convertStringToList(paymentfrequencylist),
    multiplierList: convertStringToList(custommultiplier, "X"),
    pptList: convertStringToList(pptlist, " Years"),
    ppt_pt_mapper,
    products,
    productConfig,
    ...(category === SLUG_TAB_TERM && {
      pt: Number(pt),
      ppt: Number(ppt),
    }),
  };
};

export const traverseSavingGridResponse = (data, selectedProductId = null) => {
  const product = {};
  let findIndex = -1;

  const filteredList = selectedProductId
    ? data.filter((item) => item.pid == selectedProductId)
    : data.filter((item) => item.defaultproduct == 1);

  filteredList.map((item, index) => {
    if (findIndex === -1 && item.default_value == 1) {
      product.planOption = item.calculationfactor == 1 ? "B" : "A";
      product.optionId = item.calculationfactor == 1 ? "2" : "1";
      product.premium =
        item.calculationfactor == 1 ? null : Number(item.premium);
      product.corpus =
        item.calculationfactor == 1 ? item.maturity_sum_assured : null;
      findIndex = index;
    }
  });

  return product;
};

export const rebuildPremiumOptions = ({
  premiumOptions: list = [],
  paymentFrequency,
}) => {
  const premiumOptions = [];

  list.map((item) => {
    premiumOptions.push({
      title:
        mapping.premium[item.value * mapping.paymentFrequency[paymentFrequency]]
          .title,
      value: item.value,
    });
  });

  return premiumOptions;
};

export const rebuildCorpusOptions = ({
  corpusOptions: list = [],
  category,
  education,
}) => {
  const corpusOptions = [];

  list.forEach((item) => {
    // Apply filter if category is 'term' and education is '10th'
    if (
      category === SLUG_TAB_TERM &&
      education === SLUG_EDUCATION_10TH &&
      Number(item.value) > 2500000
    ) {
      return; // Skip this item
    }
    corpusOptions.push({
      title: mapping.corpus[item.value].title,
      value: item.value,
    });
  });

  return corpusOptions;
};

export const rebuildLanguageOptions = () => {
  return getStorageItem("partnerCode") === DEFAULT_PARTNER_CODE
    ? options.language
    : options.nonMarathiLanguage;
};

export const extractPolicyTermFromMapper = (product, form) => {
  return JSON.parse(product?.ppt_pt_mapper)[form.ppt];
};

export const rebuildPolicyTermOptions = (productConfig = null, age = null) => {
  if (!age) {
    return [];
  }

  const list = productConfig?.policy_term ?? [];
  const maxCoverageAge = productConfig?.max_coverage_age ?? 60;
  const remainingYears = maxCoverageAge - age;

  return list.filter((option) => Number(option.value) <= remainingYears);
};

export const rebuildPolicyTermOptionsOld = (list = [], age = null) => {
  if (!age) {
    return [];
  }

  if (age >= 41 && age <= 45) {
    return list.filter(
      (option) => option.value === 10 || option.value === "10"
    );
  }
  return list;
};

export const isValidPolicyTermOptions = (form, productConfig = null) => {
  const age = convertToAge(form.dob);
  const list = rebuildPolicyTermOptions(productConfig, age);
  const max_coverage_age = productConfig?.max_coverage_age ?? 75;
  const ppt = form.ppt;
  const policy_term_textbox = productConfig?.policy_term_textbox ?? 0;

  let isValid = false;

  if (!list.length || !ppt) {
    return isValid;
  }

  if (policy_term_textbox) {
    isValid =
      ppt >= 10 &&
      ppt <= maximumPolicyTenure(form.dob, max_coverage_age) &&
      Number(ppt) % 1 === 0;
  } else {
    list.forEach((option) => {
      if (Number(option.value) === Number(ppt)) {
        isValid = true;
      }
    });
  }

  return isValid;
};

export const isValidLifeCoverOptions = (form, productConfig) => {
  const list = rebuildCorpusOptions(form);

  const corpus = form.corpus;
  const life_coverege_textbox = productConfig?.life_coverege_textbox ?? 0;

  let isValid = false;

  if (!list.length || !corpus) {
    return isValid;
  }

  if (life_coverege_textbox) {
    isValid =
      corpus >= list[0]?.value &&
      corpus <= list[list.length - 1]?.value &&
      (corpus % 50000 === 0 || corpus % 100000 === 0);
  } else {
    list.forEach((option) => {
      if (Number(option.value) === Number(corpus)) {
        isValid = true;
      }
    });
  }

  return isValid;
};

export const buildProductList = (list) => {
  const uniqueProducts = new Map();

  (list || []).forEach((product) => {
    if (!uniqueProducts.has(product.id)) {
      uniqueProducts.set(product.id, {
        id: product.id,
        displayname: product.displayname,
        name: product.name,
        isdefault: product.isdefault,
      });
    }
  });

  return Array.from(uniqueProducts.values());
};

export const getDefaultProductId = (list) => {
  const defaultProduct = list.find((product) => product.isdefault === 1);

  return defaultProduct ? defaultProduct.id : null;
};

export const maximumPolicyTenure = (dob = null, maxCoverageAge = 75) => {
  const age = convertToAge(dob);
  let maxTenure = 0;

  if (maxCoverageAge && age > 0) {
    maxTenure = maxCoverageAge - age;
  }

  if (maxTenure > 30) {
    maxTenure = 30;
  }

  return maxTenure;
};
